import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AUTHORIZED_SERVICES } from "@/lib/status";
import { Plus, Factory, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function ManufacturersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/manufacturers"); setItems(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ManufacturerDialog onSaved={load} />
      </div>
      {loading ? <p className="text-slate-400 text-sm">Carregando...</p>
        : items.length === 0 ? (
          <div className="text-center py-16" data-testid="manufacturers-empty">
            <Factory className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nenhum fabricante cadastrado.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2" data-testid="manufacturers-list">
            {items.map((m) => (
              <Card key={m.id} className="border-slate-200" data-testid={`manufacturer-${m.id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-11 w-11 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0"><Factory className="h-5 w-5 text-[#1e7be0]" /></div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{m.name} · <span className="text-[#1e7be0]">{m.brand}</span></p>
                      <p className="text-xs text-slate-500 truncate">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
                    </div>
                  </div>
                  <ManufacturerDialog manufacturer={m} onSaved={load} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}

function ManufacturerDialog({ manufacturer, onSaved }) {
  const isEdit = !!manufacturer;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", brand: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(isEdit
        ? { name: manufacturer.name || "", email: manufacturer.email || "", phone: manufacturer.phone || "", password: "", brand: manufacturer.brand || "" }
        : { name: "", email: "", phone: "", password: "", brand: "" });
    }
  }, [open, manufacturer, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand) { toast.error("Selecione a marca."); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/manufacturers/${manufacturer.id}`, { name: form.name, email: form.email, phone: form.phone, brand: form.brand });
        toast.success("Fabricante atualizado!");
      } else {
        await api.post("/manufacturers", { name: form.name, email: form.email, phone: form.phone, password: form.password, brand: form.brand });
        toast.success("Fabricante criado!");
      }
      setOpen(false); onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit
          ? <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-manufacturer-${manufacturer.id}`}><Pencil className="h-4 w-4" /></Button>
          : <Button className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="add-manufacturer-button"><Plus className="h-4 w-4" /> Cadastrar fabricante</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="manufacturer-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Editar fabricante" : "Cadastrar fabricante"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Nome *</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="manufacturer-name-input" /></div>
          <div className="space-y-1.5"><Label>Marca *</Label>
            <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}>
              <SelectTrigger data-testid="manufacturer-brand-select"><SelectValue placeholder="Selecione a marca" /></SelectTrigger>
              <SelectContent>{AUTHORIZED_SERVICES.map((b) => <SelectItem key={b} value={b} data-testid={`manufacturer-brand-${b}`}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>E-mail *</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="manufacturer-email-input" /></div>
            <div className="space-y-1.5"><Label>Telefone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="manufacturer-phone-input" /></div>
          </div>
          {!isEdit && (
            <div className="space-y-1.5"><Label>Senha *</Label>
              <Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mín. 6 caracteres" data-testid="manufacturer-password-input" /></div>
          )}
          <DialogFooter>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="manufacturer-save-button">
              {busy ? "Salvando..." : (isEdit ? "Salvar" : "Criar fabricante")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
